import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { EstadoJogo } from '@core/motor/estados';
import { MSG_CARREGANDO, NOME_MODO } from '@core/motor/mensagens';
import { NIVEIS_POR_FASE, tituloDaFase } from '@core/modos/palavredo';
import type { DownloadsDoTabuleiro } from './BarraSuperior';
import { Confete } from './Confete';
import { Marca } from './Marca';
import { DURACOES, palavrasEscritas, rotularDuracao } from '@core/modos/corre';
import { gradeCompartilhavel, textoCompartilhavel } from '@core/termo/compartilhar';
import type { EstadoLetra } from '@core/termo/avaliacao';
import { descobriu } from '@core/termo/partida';
import { tamanhoDoDia } from '@core/termo/dia';
import { BONUS_CRUZAMENTO, JOGADAS_TRAMA, jogadasDe } from '@core/modos/trama';
import { modoDisponivel, type ModoJogo } from '@core/tipos';

/**
 * Diálogo modal sobre `<dialog>` nativo.
 *
 * `showModal()` já entrega o que T09-03 e T09-04 pedem: foco preso enquanto
 * aberto e `Esc` equivalente a cancelar. Nada de reimplementar armadilha de
 * foco à mão.
 */
export function Dialogo({
  aberto,
  aoFechar,
  rotuladoPor,
  classe,
  children,
}: {
  aberto: boolean;
  aoFechar: () => void;
  rotuladoPor: string;
  /** Modificador para o diálogo que precisa de outra caixa. */
  classe?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const elemento = ref.current;
    if (elemento === null) return;

    if (aberto && !elemento.open) elemento.showModal();
    if (!aberto && elemento.open) elemento.close();
  }, [aberto]);

  return (
    <dialog
      ref={ref}
      className={classe === undefined ? 'dialogo' : `dialogo ${classe}`}
      aria-labelledby={rotuladoPor}
      onCancel={(e) => {
        e.preventDefault();
        aoFechar();
      }}
      onClose={aoFechar}
    >
      {aberto && children}
    </dialog>
  );
}

/**
 * O jogo compilando as palavras (§17).
 *
 * São 239 mil, e em aparelho modesto isso leva segundos: sem nada na tela, o
 * intervalo se parece com uma página quebrada. O modal fecha sozinho quando o
 * índice fica utilizável — inclusive no parcial, que já dá para jogar (§16).
 *
 * Não tem como fechar, e é de propósito: não há nada por baixo para usar. `Esc`
 * bate no `preventDefault` de `Dialogo`, e a espera não vira uma decisão.
 */
export function DialogoCarregando({ aberto }: { aberto: boolean }) {
  return (
    <Dialogo
      aberto={aberto}
      aoFechar={NADA}
      rotuladoPor="titulo-carregando"
      classe="dialogo--carregando"
    >
      <Marca decorativa />
      <p className="carregando__texto" id="titulo-carregando">
        {MSG_CARREGANDO}
      </p>
      {/* Indeterminada: o worker não reporta progresso, e uma barra que
          fingisse porcentagem estaria inventando o número. */}
      <span className="carregando__barra" aria-hidden="true" />
    </Dialogo>
  );
}

/** Diálogo que não fecha: a espera não é uma decisão de quem espera. */
const NADA = () => {};

/**
 * Fim de fase do Palavredo (§22.7).
 *
 * O único momento do jogo em que um tabuleiro é apagado de propósito, e por
 * isso o único que pede cerimônia. Os dois downloads (§7.5) estão aqui porque
 * é aqui que eles querem estar: no painel do `i` são item de menu, e no
 * instante em que o tabuleiro vai embora são o troféu da fase.
 *
 * Baixar não fecha o diálogo — quem quer o PNG frequentemente também quer o
 * GIF, e fechar depois do primeiro cobraria o caminho inteiro de novo.
 */
export function DialogoFase({
  aberto,
  fase,
  proximaFase,
  baixar,
  aoVerTabuleiro,
  aoComecar,
}: {
  aberto: boolean;
  fase: number;
  proximaFase: number;
  /** Ausente enquanto não há tabuleiro — não deveria acontecer aqui, mas o
      tipo é o mesmo que a barra usa, e mentir sobre ele custaria mais. */
  baixar?: DownloadsDoTabuleiro | undefined;
  aoVerTabuleiro: () => void;
  aoComecar: () => void;
}) {
  const comecarRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (aberto) comecarRef.current?.focus();
  }, [aberto]);

  return (
    <Dialogo
      aberto={aberto}
      aoFechar={aoVerTabuleiro}
      rotuladoPor="titulo-fase"
      classe="dialogo--fase"
    >
      {/* Só enquanto o diálogo está aberto: montar o confete junto é o que faz
          a explosão acontecer na hora certa, sem temporizador nenhum. */}
      <Confete />

      <p className="fase__parabens">Parabéns!</p>
      <h2 className="dialogo__titulo" id="titulo-fase">
        {tituloDaFase(fase)}
      </h2>

      <p className="dialogo__texto">
        {NIVEIS_POR_FASE} níveis num tabuleiro só. Ele fecha aqui — leve uma cópia antes, se quiser.
      </p>

      {baixar !== undefined && (
        <div className="fase__saidas">
          <button type="button" className="botao" onClick={baixar.png}>
            Baixar imagem
          </button>
          <button type="button" className="botao" onClick={baixar.gif}>
            Baixar GIF
          </button>
        </div>
      )}

      <div className="dialogo__acoes">
        <button type="button" className="botao" onClick={aoVerTabuleiro}>
          Ver o tabuleiro
        </button>
        <button
          type="button"
          className="botao botao--primario"
          ref={comecarRef}
          onClick={aoComecar}
        >
          Começar Fase {proximaFase}
        </button>
      </div>
    </Dialogo>
  );
}

/** Confirmação de reinício (T09). */
export function DialogoRecomecar({
  aberto,
  aoCancelar,
  aoConfirmar,
}: {
  aberto: boolean;
  aoCancelar: () => void;
  aoConfirmar: () => void;
}) {
  const cancelarRef = useRef<HTMLButtonElement>(null);

  // T09-02: `Cancelar` recebe o foco inicial.
  useEffect(() => {
    if (aberto) cancelarRef.current?.focus();
  }, [aberto]);

  return (
    <Dialogo aberto={aberto} aoFechar={aoCancelar} rotuladoPor="titulo-recomecar">
      <h2 className="dialogo__titulo" id="titulo-recomecar">
        Recomeçar?
      </h2>
      <p className="dialogo__texto">A cadeia atual será apagada deste dispositivo.</p>
      <div className="dialogo__acoes">
        <button type="button" className="botao" ref={cancelarRef} onClick={aoCancelar}>
          Cancelar
        </button>
        <button type="button" className="botao botao--primario" onClick={aoConfirmar}>
          Recomeçar
        </button>
      </div>
    </Dialogo>
  );
}

/* ── Figuras da ajuda ──────────────────────────────────────────────────── */

/**
 * Recorte de tabuleiro desenhado com as classes do tabuleiro de verdade.
 *
 * É a tela do jogo, e não uma imagem dela: as mesmas `.celula--base`,
 * `.celula--ponte`, `.celula--sugestao` e as mesmas marcas do dia, arrumadas
 * num grid em vez de posicionamento absoluto. Sai melhor que um print — não
 * pesa no bundle, acompanha o tema claro e o escuro, e nunca envelhece em
 * relação ao jogo: quando a cor de uma célula muda, a figura muda junto.
 *
 * A grade inteira é `aria-hidden`: quem lê com leitor de tela recebe a
 * legenda, que diz em palavras o que o desenho mostra (§13).
 */
type TipoCelulaFigura = 'base' | 'ponte' | 'sugestao' | 'draft' | 'historico' | 'origem';

interface MarcadorFigura {
  readonly classe: 'livre' | 'usada' | 'aposta';
  readonly texto: string;
}

interface CelulaFigura {
  readonly coluna: number;
  readonly linha: number;
  readonly letra: string;
  readonly tipo: TipoCelulaFigura;
  /** §23: verde, amarelo, cinza. */
  readonly marca?: EstadoLetra;
  readonly marcador?: MarcadorFigura;
}

interface Figura {
  readonly colunas: number;
  readonly celulas: readonly CelulaFigura[];
  /** T02-01: letra sem vertical possível, marcada com um ponto. */
  readonly semCruzamento?: readonly { readonly coluna: number; readonly linha: number }[];
  readonly legenda: string;
}

const CLASSE_FIGURA: Readonly<Record<TipoCelulaFigura, string>> = {
  base: 'celula celula--base',
  ponte: 'celula celula--ponte',
  sugestao: 'celula celula--sugestao',
  draft: 'celula celula--draft',
  historico: 'celula celula--historico',
  origem: 'celula celula--historico celula--origem',
};

function FiguraTabuleiro({ figura }: { figura: Figura }) {
  return (
    <figure className="figura">
      <div
        className="figura__grade"
        style={{ gridTemplateColumns: `repeat(${figura.colunas}, var(--lado-figura))` }}
        aria-hidden="true"
      >
        {figura.celulas.map((c) => (
          <span
            key={`${c.coluna}:${c.linha}`}
            className="figura__casa"
            style={{ gridColumn: c.coluna, gridRow: c.linha }}
          >
            <span className={CLASSE_FIGURA[c.tipo]} data-marca={c.marca}>
              {c.letra}
            </span>
            {c.marcador !== undefined && (
              <span className={`figura__marcador figura__marcador--${c.marcador.classe}`}>
                {c.marcador.texto}
              </span>
            )}
          </span>
        ))}

        {figura.semCruzamento?.map((c) => (
          <span
            key={`x${c.coluna}:${c.linha}`}
            className="figura__casa"
            style={{ gridColumn: c.coluna, gridRow: c.linha }}
          >
            <span className="indisponivel">·</span>
          </span>
        ))}
      </div>

      <figcaption className="figura__legenda">{figura.legenda}</figcaption>
    </figure>
  );
}

/* ── Como jogar ────────────────────────────────────────────────────────── */

interface PaginaAjuda {
  readonly id: string;
  readonly titulo: string;
  readonly figura: Figura;
  /**
   * A explicação, em parágrafos.
   *
   * Prosa, e não tópicos: em lista, cada regra chegava solta e o leitor
   * tinha de descobrir sozinho como uma leva à outra. O que faz o jogo ser
   * entendido é justamente a ligação entre elas — a palavra abre verticais,
   * a vertical vira ponte, a ponte obriga a próxima palavra.
   */
  readonly paragrafos: readonly ReactNode[];
  /** Presente nas páginas de modo; ausente nas duas do jogo em si. */
  readonly modo?: ModoJogo;
}

const LIVRE: MarcadorFigura = { classe: 'livre', texto: '○' };
const USADA: MarcadorFigura = { classe: 'usada', texto: '×' };

/**
 * A ajuda em páginas (T01-07).
 *
 * As duas primeiras são o jogo, que é o mesmo em todo modo — quem chega pelo
 * modo do dia ainda precisa aprender a encaixar uma palavra. Depois vem uma
 * página por modo, na mesma ordem do diálogo de escolha.
 */
const PAGINAS: readonly PaginaAjuda[] = [
  {
    id: 'tabuleiro',
    titulo: 'O tabuleiro',
    figura: {
      colunas: 4,
      celulas: [
        { coluna: 1, linha: 1, letra: 'A', tipo: 'draft' },
        { coluna: 2, linha: 1, letra: 'M', tipo: 'draft' },
        { coluna: 3, linha: 1, letra: 'O', tipo: 'draft' },
        { coluna: 4, linha: 1, letra: 'R', tipo: 'draft' },

        { coluna: 1, linha: 2, letra: 'S', tipo: 'sugestao' },
        { coluna: 1, linha: 3, letra: 'A', tipo: 'sugestao' },

        { coluna: 2, linha: 2, letra: 'E', tipo: 'sugestao' },
        { coluna: 2, linha: 3, letra: 'S', tipo: 'sugestao' },
        { coluna: 2, linha: 4, letra: 'A', tipo: 'sugestao' },

        { coluna: 4, linha: 2, letra: 'I', tipo: 'sugestao' },
        { coluna: 4, linha: 3, letra: 'O', tipo: 'sugestao' },
      ],
      semCruzamento: [{ coluna: 3, linha: 2 }],
      legenda:
        'AMOR sendo escrita na horizontal, com ASA, MESA e RIO descendo dela. Sob o O, um ponto: ali nenhuma vertical cabe.',
    },
    paragrafos: [
      'Tudo começa com uma palavra sua, escrita na horizontal, de uma a doze letras. Enquanto você digita, o jogo procura no dicionário palavras que possam descer cruzando cada uma das suas letras, e mostra as que encontrou — são as verticais mais claras da figura.',
      'Elas saem da palavra que você escreveu, então mudar uma letra muda as verticais junto. Se não gostou das opções que apareceram, o caminho é trocar a sua palavra: são ela e o dicionário que decidem o que cabe ali.',
      'Quando uma letra não tem nenhuma vertical possível, no lugar da coluna aparece um ponto. Ele está ali de propósito, para você saber que a casa foi olhada e não havia palavra — e não que o jogo simplesmente esqueceu daquela letra.',
    ],
  },
  {
    id: 'jogada',
    titulo: 'A jogada',
    figura: {
      colunas: 4,
      celulas: [
        { coluna: 1, linha: 1, letra: 'A', tipo: 'base' },
        { coluna: 2, linha: 1, letra: 'M', tipo: 'base', marcador: USADA },
        { coluna: 3, linha: 1, letra: 'O', tipo: 'base' },
        { coluna: 4, linha: 1, letra: 'R', tipo: 'base' },

        { coluna: 2, linha: 2, letra: 'E', tipo: 'ponte', marcador: LIVRE },
        { coluna: 2, linha: 3, letra: 'S', tipo: 'ponte', marcador: LIVRE },
        { coluna: 2, linha: 4, letra: 'A', tipo: 'ponte', marcador: LIVRE },
      ],
      legenda:
        'MESA escolhida virou ponte. O × marca a interseção já usada, e os ○ são as letras por onde a próxima palavra pode sair.',
    },
    paragrafos: [
      'Não existe botão de confirmar. Assim que a sua palavra é reconhecida, escolher uma das verticais é o que fecha a jogada: a sua palavra se fixa no tabuleiro e a vertical escolhida vira ponte — a ligação para o que vem depois.',
      'Na ponte, você escolhe a letra por onde quer continuar. São as marcadas com ○. O × marca a casa em que a ponte cruzou a sua palavra: aquela ligação já foi usada e não serve uma segunda vez.',
      'A palavra seguinte precisa conter a letra que você escolheu e caber no espaço que sobrou em volta dela. É essa exigência dupla que dá o jogo: não basta lembrar de uma palavra com T, ela também tem de caber. Quando não cabe, o jogo diz qual foi o impedimento, e daí é só corrigir.',
      <>
        Para voltar atrás no teclado físico, <kbd>Ctrl</kbd>+<kbd>Z</kbd> apaga o texto que você
        está escrevendo; apertado de novo com o campo já vazio, desfaz a jogada inteira.
      </>,
    ],
  },
  {
    id: 'termo',
    modo: 'termo',
    titulo: NOME_MODO.termo,
    figura: {
      colunas: 4,
      celulas: [
        { coluna: 1, linha: 1, letra: 'A', tipo: 'base', marca: 'certa' },
        { coluna: 2, linha: 1, letra: 'M', tipo: 'base', marca: 'certa' },
        { coluna: 3, linha: 1, letra: 'O', tipo: 'base', marca: 'presente' },
        { coluna: 4, linha: 1, letra: 'R', tipo: 'base', marca: 'ausente' },

        { coluna: 3, linha: 2, letra: 'L', tipo: 'ponte' },
        { coluna: 3, linha: 3, letra: 'H', tipo: 'ponte' },
        { coluna: 3, linha: 4, letra: 'O', tipo: 'ponte' },
      ],
      legenda:
        'AMOR contra uma palavra do dia que começa por AM: A e M verdes, O amarelo, R cinza. O O continua amarelo mesmo sendo o cruzamento de OLHO.',
    },
    paragrafos: [
      'Todo dia há uma palavra escondida, a mesma para todo mundo. O tabuleiro continua funcionando exatamente como nas duas páginas anteriores; o que muda é que cada palavra que você escreve vale também como palpite sobre a palavra do dia.',
      'As letras do palpite ganham cor na própria casa do tabuleiro. Verde quer dizer que a letra está na posição certa; amarelo, que ela existe na palavra do dia mas em outro lugar; cinza, que ela não está lá. Na figura, AMOR foi escrita contra uma palavra que começa por AM — daí o A e o M verdes —, que tem um O em outra posição e não tem R.',
      'A cor vale inclusive na letra em que uma vertical cruza. Aquela casa pertence às duas palavras ao mesmo tempo, e quem manda nela é a sua: é ali que o amarelo mais costuma aparecer, porque a letra que a vertical aproveitou dificilmente está na posição certa da resposta.',
      'Não é preciso acentuar — escreva ACAO e o jogo entende AÇÃO. O teclado guarda o que você já descobriu de cada letra, e não há limite de tentativas: como a letra escolhida na ponte já obriga a próxima palavra, um teto de tentativas tornaria alguns dias impossíveis só por posicionamento.',
      <>
        Quando você acerta, o tabuleiro não vai embora. Dá para continuar jogando nele no{' '}
        {NOME_MODO.zen}, agora sem palavra escondida por cima.
      </>,
    ],
  },
  {
    id: 'zen',
    modo: 'zen',
    titulo: NOME_MODO.zen,
    figura: {
      colunas: 5,
      celulas: [
        { coluna: 1, linha: 1, letra: 'A', tipo: 'origem' },
        { coluna: 2, linha: 1, letra: 'M', tipo: 'origem' },
        { coluna: 3, linha: 1, letra: 'O', tipo: 'origem' },
        { coluna: 4, linha: 1, letra: 'R', tipo: 'origem' },

        { coluna: 3, linha: 2, letra: 'L', tipo: 'historico' },
        { coluna: 3, linha: 3, letra: 'H', tipo: 'historico' },

        { coluna: 3, linha: 4, letra: 'O', tipo: 'base' },
        { coluna: 4, linha: 4, letra: 'C', tipo: 'base' },
        { coluna: 5, linha: 4, letra: 'A', tipo: 'base' },
      ],
      legenda:
        'A cadeia crescendo: AMOR abriu OLHO, e do O de baixo saiu OCA. A primeira palavra guarda a moldura de origem.',
    },
    paragrafos: [
      'O mesmo jogo, sem nada cobrando: não há placar, não há cronômetro e não há fim. A cadeia continua enquanto você quiser, e o que resta no final é o tabuleiro que você construiu.',
      'Para o tabuleiro não virar um bloco de letras conforme cresce, as palavras antigas vão perdendo tinta e cedendo destaque à que está sendo escrita — sem deixar de ser legíveis. A primeira de todas é a exceção: ela guarda a moldura própria, porque é dela que o tabuleiro inteiro saiu.',
    ],
  },
  {
    id: 'corre',
    modo: 'corre',
    titulo: NOME_MODO.corre,
    figura: {
      colunas: 4,
      celulas: [
        { coluna: 1, linha: 1, letra: 'A', tipo: 'historico' },
        { coluna: 2, linha: 1, letra: 'M', tipo: 'historico' },
        { coluna: 3, linha: 1, letra: 'O', tipo: 'historico' },
        { coluna: 4, linha: 1, letra: 'R', tipo: 'historico' },

        { coluna: 2, linha: 2, letra: 'E', tipo: 'ponte' },
        { coluna: 2, linha: 3, letra: 'S', tipo: 'ponte' },

        { coluna: 2, linha: 4, letra: 'S', tipo: 'draft' },
        { coluna: 3, linha: 4, letra: 'O', tipo: 'draft' },
        { coluna: 4, linha: 4, letra: 'L', tipo: 'draft' },
      ],
      legenda:
        'Duas palavras já no tabuleiro e a terceira sendo escrita, com o cronômetro correndo.',
    },
    paragrafos: [
      'Aqui o tempo é fixo e o placar é quantas palavras você consegue encaixar dentro dele. A duração — 30 segundos, 1 minuto ou 2 — é escolhida junto com o modo, num toque só.',
      'O cronômetro não começa quando você escolhe o modo, e sim na primeira letra que você digita: o tempo de se ajeitar não é seu. Dali em diante ele corre no relógio de parede, então fechar a aba não congela a partida e voltar depois não devolve segundos.',
      'Só jogada confirmada conta. A palavra que ficou pela metade quando o tempo acabou não entra no placar — no tabuleiro só existe o que foi fechado escolhendo uma vertical.',
    ],
  },
  {
    id: 'trama',
    modo: 'trama',
    titulo: NOME_MODO.trama,
    figura: {
      colunas: 4,
      celulas: [
        { coluna: 1, linha: 1, letra: 'A', tipo: 'base' },
        { coluna: 2, linha: 1, letra: 'M', tipo: 'base', marcador: USADA },
        { coluna: 3, linha: 1, letra: 'O', tipo: 'base' },
        { coluna: 4, linha: 1, letra: 'R', tipo: 'base' },

        { coluna: 2, linha: 2, letra: 'E', tipo: 'ponte', marcador: LIVRE },
        {
          coluna: 2,
          linha: 3,
          letra: 'X',
          tipo: 'ponte',
          marcador: { classe: 'aposta', texto: '×3' },
        },
        { coluna: 2, linha: 4, letra: 'A', tipo: 'ponte', marcador: LIVRE },
      ],
      legenda:
        'Na ponte, o X vale multiplicador 3: continuar por ele é apostar que cabe uma palavra com X no espaço que sobrou.',
    },
    paragrafos: [
      <>
        A partida tem {JOGADAS_TRAMA} jogadas, e o placar é o quanto você consegue tirar delas. Duas
        coisas pontuam, e as duas são decisões que no {NOME_MODO.zen} não teriam consequência
        nenhuma.
      </>,
      <>
        A primeira é atravessar. Toda jogada cruza pelo menos uma palavra; cada travessia além dessa
        obrigatória vale {BONUS_CRUZAMENTO} pontos. Ou seja: encaixar a palavra num lugar apertado,
        onde ela toca várias outras, vale mais do que estendê-la para o vazio.
      </>,
      'A segunda é a letra por onde você escolhe continuar, e essa é uma aposta. Quanto mais rara a letra no dicionário, maior o multiplicador que ela deixa valendo para a palavra seguinte — na figura, sair pelo X vale três vezes. A raridade sai do próprio corpus, e não de uma tabela escrita à mão.',
      'A aposta é cobrada quando a palavra vem, e se perde junto com a jogada se ela não vier. Apostar alto e não achar palavra nenhuma não custa pontos: custa a chance de ter feito muitos.',
    ],
  },
  {
    id: 'palavredo',
    modo: 'palavredo',
    titulo: NOME_MODO.palavredo,
    /*
     * Um circuito, que é o objetivo que ninguém adivinha só pelo nome: RAMO e
     * OLHO na horizontal, RATO e OSSO descendo, e as quatro se encontrando nos
     * cantos. O miolo fica vazio de propósito — o que fecha o retângulo são os
     * lados, e desenhá-lo cheio faria parecer que é preciso preencher tudo.
     */
    figura: {
      colunas: 4,
      celulas: [
        { coluna: 1, linha: 1, letra: 'R', tipo: 'base' },
        { coluna: 2, linha: 1, letra: 'A', tipo: 'base' },
        { coluna: 3, linha: 1, letra: 'M', tipo: 'base' },
        { coluna: 4, linha: 1, letra: 'O', tipo: 'base' },

        { coluna: 1, linha: 2, letra: 'A', tipo: 'ponte' },
        { coluna: 1, linha: 3, letra: 'T', tipo: 'ponte' },
        { coluna: 1, linha: 4, letra: 'O', tipo: 'ponte' },

        { coluna: 4, linha: 2, letra: 'S', tipo: 'ponte' },
        { coluna: 4, linha: 3, letra: 'S', tipo: 'ponte' },
        { coluna: 4, linha: 4, letra: 'O', tipo: 'ponte' },

        { coluna: 2, linha: 4, letra: 'L', tipo: 'base' },
        { coluna: 3, linha: 4, letra: 'H', tipo: 'base' },
      ],
      legenda:
        'RAMO em cima e OLHO embaixo, RATO à esquerda e OSSO à direita: as quatro se encontram nos cantos e fecham um circuito.',
    },
    paragrafos: [
      'Níveis sem fim, e cada um pede uma coisa. Pode ser escrever palavras terminadas em ADO, começadas por TRA, com Ç, com uma letra repetida, com dois R — ou com ditongo, com hiato, ou ainda fechar um circuito. Do quarto nível em diante vêm dois pedidos ao mesmo tempo.',
      <>
        Ditongo são duas vogais na mesma sílaba, como o <strong>AI</strong> de PAI ou o{' '}
        <strong>ÃO</strong> de PÃO. Hiato são duas vogais que se separam, como o{' '}
        <strong>O-E</strong> de POETA ou o <strong>A-Í</strong> de SAÍDA — é para isso que serve o
        acento em PAÍS.
      </>,
      'Circuito é o da figura: duas palavras suas atravessando as mesmas duas pontes, formando um retângulo fechado. É o objetivo que exige planejar o espaço em vez de lembrar de vocabulário, e por isso só aparece a partir do terceiro nível.',
      <>
        O tabuleiro <strong>não é apagado a cada nível</strong>: ele cresce por {NIVEIS_POR_FASE}{' '}
        níveis seguidos, e o que muda no meio do caminho é só o que se cobra dele. A contagem de
        cada objetivo, essa sim, começa do zero em cada nível.
      </>,
      <>
        Esses {NIVEIS_POR_FASE} níveis são uma <strong>fase</strong>. Ao fechar a última, o jogo
        parabeniza, oferece levar o tabuleiro como imagem ou GIF, e só então limpa a tela para a
        fase seguinte — que começa vazia e mais dura. É o único momento em que um tabuleiro seu é
        apagado, e quem manda apagar é você.
      </>,
      'O progresso fica salvo neste aparelho, em chave própria: dá para ir jogar outro modo, fechar a aba e voltar dias depois na fase e no nível em que parou. Só `Recomeçar` volta ao começo.',
    ],
  },
];

/** Ajuda curta em páginas, sem abandonar a sessão (T01-07). */
export function DialogoAjuda({
  aberto,
  dev,
  aoFechar,
}: {
  aberto: boolean;
  /** §22.5: fora do portão, Nitro e Átrio não existem — nem na ajuda. */
  dev: boolean;
  aoFechar: () => void;
}) {
  const paginas = PAGINAS.filter((p) => p.modo === undefined || modoDisponivel(p.modo, dev));
  const [indice, setIndice] = useState(0);

  /* Reabrir a ajuda começa do começo: a página em que se parou da última vez
     não é onde a próxima dúvida começa. */
  useEffect(() => {
    if (aberto) setIndice(0);
  }, [aberto]);

  const pagina = paginas[indice] ?? paginas[0];
  if (pagina === undefined) return null;

  return (
    <Dialogo aberto={aberto} aoFechar={aoFechar} rotuladoPor="titulo-ajuda" classe="dialogo--ajuda">
      <h2 className="dialogo__titulo" id="titulo-ajuda">
        Como jogar
      </h2>

      {/* Só o corpo rola: a navegação e o `Entendi` ficam no lugar, senão numa
          tela baixa seria preciso rolar até o fim para virar a página. */}
      <div className="ajuda__corpo">
        <h3 className="ajuda__pagina">{pagina.titulo}</h3>

        <FiguraTabuleiro figura={pagina.figura} />

        <div className="dialogo__texto ajuda__prosa">
          {pagina.paragrafos.map((paragrafo, i) => (
            <p key={i}>{paragrafo}</p>
          ))}
        </div>
      </div>

      <nav className="ajuda__navegacao" aria-label="Páginas da ajuda">
        <button
          type="button"
          className="botao"
          onClick={() => setIndice((n) => Math.max(0, n - 1))}
          disabled={indice === 0}
        >
          Anterior
        </button>

        <span className="ajuda__pontos">
          <span className="apenas-leitor">
            Página {indice + 1} de {paginas.length}: {pagina.titulo}
          </span>
          {paginas.map((p, i) => (
            <span
              key={p.id}
              className="ajuda__ponto"
              data-atual={i === indice}
              aria-hidden="true"
            />
          ))}
        </span>

        <button
          type="button"
          className="botao"
          onClick={() => setIndice((n) => Math.min(paginas.length - 1, n + 1))}
          disabled={indice === paginas.length - 1}
        >
          Próxima
        </button>
      </nav>

      <div className="dialogo__acoes">
        <button type="button" className="botao botao--primario" onClick={aoFechar}>
          Entendi
        </button>
      </div>
    </Dialogo>
  );
}

/**
 * Os modos, na ordem em que a exigência cresce (§22): nenhuma, tempo, cálculo.
 * O Nitro é o único com uma segunda escolha, e ela vem no próprio cartão — um
 * toque resolve modo e duração de uma vez.
 */
const MODOS: readonly {
  readonly id: ModoJogo;
  readonly resumo: string;
  readonly duracoes?: readonly number[];
}[] = [
  {
    id: 'termo',
    resumo:
      'O jogo de sempre, com uma palavra escondida por dia. Cada palavra que você escreve no tabuleiro também é um palpite: verde é letra no lugar certo, amarelo é letra que existe noutra posição.',
  },
  {
    id: 'zen',
    resumo:
      'Sem placar, sem fim. A cadeia continua enquanto você quiser, e o que fica é o tabuleiro que você construiu.',
  },
  {
    id: 'corre',
    resumo:
      'Contra o relógio: quantas palavras você encaixa antes do tempo acabar. O cronômetro só começa na primeira letra.',
    duracoes: DURACOES,
  },
  {
    id: 'trama',
    resumo:
      'Dez jogadas. Atravessar mais de uma palavra vale pontos, e a letra que você escolhe para continuar é uma aposta: quanto mais rara, maior o multiplicador da palavra seguinte.',
  },
  {
    id: 'palavredo',
    resumo: `Níveis sem fim com um desafio de cada vez. ${NIVEIS_POR_FASE} níveis formam uma fase, e o tabuleiro só fecha na virada de cada fase. O progresso fica salvo neste aparelho.`,
  },
];

/**
 * Escolha do modo (§22, T10).
 *
 * Trocar de modo recomeça a partida, e o diálogo diz isso antes: as regras não
 * podem mudar debaixo de uma cadeia já construída.
 */
export function DialogoModos({
  aberto,
  modoAtual,
  dev,
  aoFechar,
  aoEscolher,
}: {
  aberto: boolean;
  modoAtual: ModoJogo;
  /** §22.5: fora do modo de desenvolvimento, Nitro e Átrio não aparecem. */
  dev: boolean;
  aoFechar: () => void;
  aoEscolher: (modo: ModoJogo, duracaoSegundos?: number) => void;
}) {
  return (
    <Dialogo aberto={aberto} aoFechar={aoFechar} rotuladoPor="titulo-modos">
      <h2 className="dialogo__titulo" id="titulo-modos">
        Modos de jogo
      </h2>

      <ul className="modos">
        {MODOS.filter((modo) => modoDisponivel(modo.id, dev)).map((modo) => {
          const atual = modo.id === modoAtual;
          const cabecalho = (
            <>
              <span className="modo__nome">
                {NOME_MODO[modo.id]}
                {/* O espaço é explícito: sem ele o JSX cola as duas partes e o
                    leitor de tela anuncia `Bistrôem curso`. */}
                {atual && (
                  <>
                    {' '}
                    <span className="modo__atual">em curso</span>
                  </>
                )}
              </span>
              <span className="modo__resumo">{modo.resumo}</span>
            </>
          );

          // Com durações, o cartão não é botão: os botões são as durações, e
          // aninhar um dentro do outro seria HTML inválido e foco duplicado.
          if (modo.duracoes === undefined) {
            return (
              <li key={modo.id}>
                <button
                  type="button"
                  className="modo"
                  aria-current={atual ? 'true' : undefined}
                  onClick={() => aoEscolher(modo.id)}
                >
                  {cabecalho}
                </button>
              </li>
            );
          }

          return (
            <li key={modo.id}>
              <div className="modo modo--estatico" aria-current={atual ? 'true' : undefined}>
                {cabecalho}
                <span className="modo__duracoes">
                  {modo.duracoes.map((segundos) => (
                    <button
                      type="button"
                      key={segundos}
                      className="botao modo__duracao"
                      onClick={() => aoEscolher(modo.id, segundos)}
                    >
                      {rotularDuracao(segundos)}
                    </button>
                  ))}
                </span>
              </div>
            </li>
          );
        })}
      </ul>

      <p className="dialogo__credito">Trocar de modo começa uma partida nova.</p>

      <div className="dialogo__acoes">
        <button type="button" className="botao" onClick={aoFechar}>
          Fechar
        </button>
      </div>
    </Dialogo>
  );
}

/** Fim de partida, nos modos que têm fim (§22.2, §22.3, §23.1). */
export function DialogoFim({
  aberto,
  estado,
  aoVerTabuleiro,
  aoJogarDeNovo,
  aoContinuar,
}: {
  aberto: boolean;
  estado: EstadoJogo;
  aoVerTabuleiro: () => void;
  aoJogarDeNovo: () => void;
  /** §22.6: segue o mesmo tabuleiro no Bistrô, em vez de começar outro. */
  aoContinuar: () => void;
}) {
  const jogarRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (aberto) jogarRef.current?.focus();
  }, [aberto]);

  const corrida = estado.modo === 'corre';
  const dia = estado.modo === 'termo';
  const palavras = palavrasEscritas(estado.cadeia);
  const achou = descobriu(estado.cadeia, estado.resposta);

  const [copiado, setCopiado] = useState(false);

  /**
   * Compartilhar o resultado (§23.5).
   *
   * No celular, `navigator.share` abre a folha do sistema e o texto vai para o
   * WhatsApp, o e-mail ou o que a pessoa tiver instalado — que é onde o
   * resultado de um jogo diário costuma ir. Onde a API não existe, sobra a
   * área de transferência, e o rótulo do botão passa a dizer `Copiado`.
   *
   * Cancelar a folha do sistema **não** cai para a cópia: quem fechou a folha
   * desistiu de compartilhar, e encher a área de transferência por conta
   * própria seria fazer outra coisa em vez do que foi pedido. Só uma falha de
   * verdade — a API existir e recusar — passa para o plano B.
   */
  const compartilhar = useCallback(async () => {
    if (estado.dia === undefined) return;
    const texto = textoCompartilhavel(estado.cadeia, estado.dia, estado.resposta, achou);

    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ text: texto });
        return;
      } catch (erro) {
        if (erro instanceof DOMException && erro.name === 'AbortError') return;
        // Qualquer outra falha cai na cópia, logo abaixo.
      }
    }

    try {
      await navigator.clipboard?.writeText(texto);
      setCopiado(true);
    } catch {
      setCopiado(false);
    }
  }, [estado.cadeia, estado.dia, estado.resposta, achou]);

  return (
    <Dialogo aberto={aberto} aoFechar={aoVerTabuleiro} rotuladoPor="titulo-fim">
      <h2 className="dialogo__titulo" id="titulo-fim">
        {dia ? estado.resposta : corrida ? 'Fim do tempo' : 'Fim da partida'}
      </h2>

      {/* §23.1: a Trama não tem placar. A palavra do dia já está no título, e
          um número embaixo dela só serviria para comparar com o dos outros. */}
      {!dia && (
        <p className="fim__placar">
          <span className="fim__valor">{corrida ? palavras : estado.pontos}</span>
          <span className="fim__unidade">
            {corrida
              ? palavras === 1
                ? 'palavra'
                : 'palavras'
              : estado.pontos === 1
                ? 'ponto'
                : 'pontos'}
          </span>
        </p>
      )}

      {dia && estado.dia !== undefined && (
        <p className="fim__grade" aria-hidden="true">
          {gradeCompartilhavel(estado.cadeia, estado.resposta).map((linha, i) => (
            <span key={i} style={{ display: 'block' }}>
              {linha}
            </span>
          ))}
        </p>
      )}

      <p className="dialogo__texto">
        {dia ? (
          <>
            A palavra de hoje tinha {tamanhoDoDia(estado.dia ?? '')} letras. O tabuleiro não vai
            embora: dá para seguir a cadeia nele, agora sem palavra escondida, no {NOME_MODO.zen}.
          </>
        ) : corrida ? (
          <>
            Em {rotularDuracao(estado.duracaoSegundos ?? 0)}. Só jogada confirmada conta — a palavra
            que ficou no meio do caminho quando o tempo acabou não entrou.
          </>
        ) : (
          <>
            {jogadasDe(estado.cadeia)} de {JOGADAS_TRAMA} jogadas. Cada travessia além da
            obrigatória vale {BONUS_CRUZAMENTO} pontos, multiplicados pela raridade da letra que
            você escolheu para continuar.
          </>
        )}
      </p>

      <div className="dialogo__acoes">
        {dia && (
          <button type="button" className="botao" onClick={() => void compartilhar()}>
            {copiado ? 'Copiado' : 'Compartilhar'}
          </button>
        )}
        <button type="button" className="botao" onClick={aoVerTabuleiro}>
          Ver o tabuleiro
        </button>
        <button
          type="button"
          className="botao botao--primario"
          ref={jogarRef}
          onClick={dia ? aoContinuar : aoJogarDeNovo}
        >
          {dia ? 'Continuar tabuleiro' : 'Jogar de novo'}
        </button>
      </div>
    </Dialogo>
  );
}

/** Sobre o jogo: a ideia, o que ele exercita, e quem o fez. */
export function DialogoSobre({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const fecharRef = useRef<HTMLButtonElement>(null);

  // Sem isto o foco inicial cairia no primeiro link do crédito, e quem navega
  // por teclado começaria a leitura pelo rodapé do diálogo.
  useEffect(() => {
    if (aberto) fecharRef.current?.focus();
  }, [aberto]);

  return (
    <Dialogo aberto={aberto} aoFechar={aoFechar} rotuladoPor="titulo-sobre">
      <h2 className="dialogo__titulo" id="titulo-sobre">
        Sobre o Entreletras
      </h2>

      <div className="dialogo__texto">
        <p>
          Entreletras é um jogo de exploração lexical. Uma brincadeira com o acaso. Você escreve uma
          palavra, o tabuleiro responde com verticais que a cruzam, e escolher uma delas abre a
          próxima, é uma cadeia que não termina. O que fica é o tabuleiro que você construiu.
        </p>

        <p>
          A ideia é simples. E para continuar não basta lembrar uma palavra com a letra pedida, ela
          tem que caber no espaço que sobrou. Isso põe para trabalhar três coisas ao mesmo tempo: o
          vocabulário que você tem, o raciocínio espacial de encaixar uma peça num vão, e a
          flexibilidade de abandonar a primeira ideia quando ela não serve.
        </p>

        <p className="dialogo__lema">
          Inteligência híbrida: tabuleiro de palavras cruzadas que você cria junto com a máquina.
        </p>
      </div>

      <p className="dialogo__credito">
        Desenvolvido por{' '}
        <a href="https://gustavosimas.com" target="_blank" rel="noreferrer noopener">
          Gustavo Simas
        </a>
      </p>

      <div className="dialogo__acoes">
        <button type="button" className="botao botao--primario" ref={fecharRef} onClick={aoFechar}>
          Fechar
        </button>
      </div>
    </Dialogo>
  );
}
